import { clamp } from 'lodash';
import { LA_PER_APARTMENT } from '../constants';
import {
  ActivityInput,
  DEFAULT_LIVINGAREA_PER_HEATED_AREA_RATIO,
} from './energy/energy.models';
import { toEnergyActivity } from './energy/energy-activity-type.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';

/**
 * Get number of people in apartment based on number of rooms
 * Page 4: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * @param numberOfRooms
 * @returns Number of people
 */
export const getNumberOfPeopleInApartment = (numberOfRooms: number) => {
  // Force to 1-5
  switch (clamp(Math.round(numberOfRooms), 1, 5)) {
    case 1:
      return 1.42;
    case 2:
      return 1.63;
    case 3:
      return 2.18;
    case 4:
      return 2.79;
    default:
      return 3.51;
  }
};

/**
 * Get number of rooms in apartment based on living area
 * @param laApartment
 * @returns Number of rooms
 */
export const getNumberOfRoomsByLivingArea = (laApartment: number): number => {
  if (laApartment < 45) return 1;
  if (laApartment < 65) return 2;
  if (laApartment < 80) return 3;
  if (laApartment < 100) return 4;
  if (laApartment < 120) return 5;
  // För större lägenheter, räkna ut antal rum schablonmässigt
  return Math.floor((laApartment - 20) / 20);
};

/**
 * Get number of people in office or apartment based on different parameters
 * Page 4: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * @param activity
 * @param heatedArea
 * @param laPerApartment
 * @returns Number of people in total
 */
export const getOccupantCountPerM2 = (
  activity: ActivityInput | undefined,
  laPerApartment?: number,
  laPerHeatedArea: number = DEFAULT_LIVINGAREA_PER_HEATED_AREA_RATIO, // schablon
) => {
  const energyActivity = toEnergyActivity(activity);
  return energyActivity === ActivityType.Office
    ? 1 / 20
    : energyActivity === ActivityType.School
      ? 1 / 15
      : getApartmentsResidentCountPerLivingArea(laPerApartment) *
        laPerHeatedArea;
};

/**
 * Get number of people in all apartments based on number of apartments and living area per apartment
 * Page 4: https://www.boverket.se/Resources/constitutiontextstore/BEN/xml/Konsoliderad_BEN_BFS_2016_12.pdf
 * @param param0
 * @returns Number of people totally in all apartments
 */
export const getApartmentsResidentCountPerLivingArea = (
  laPerApartment?: number,
) => {
  laPerApartment = laPerApartment || LA_PER_APARTMENT; // Make sure laPerApartment is not 0
  return (
    getNumberOfPeopleInApartment(getNumberOfRoomsByLivingArea(laPerApartment)) /
    laPerApartment
  );
};
